import { List } from 'immutable'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'

import compose from 'recompose/compose'
import branch from 'recompose/branch'
import renderNothing from 'recompose/renderNothing'
import withHandlers from 'recompose/withHandlers'
import withProps from 'recompose/withProps'
import lifecycle from 'recompose/lifecycle'

import * as PlaybookActions from 'app/modules/playbook/actions'
import * as PlaybookSelectors from 'app/modules/playbook/selectors'
import * as PlaybookModels from 'app/modules/playbook/models'

import * as ClassificationActions from 'app/modules/classification/actions'
import * as ClassificationSelectors from 'app/modules/classification/selectors'

import * as UserSelectors from 'app/modules/user/selectors'
import * as RulesSelectors from 'app/modules/rules/selectors'
import * as MeetingSelectors from 'app/modules/meetings/selectors'

import { RoutingType, playbooksCopy } from 'app/modules/playbook/constants'

import Goal from './component'

const mapStateToProps = state => ({
  playbookId: PlaybookSelectors.draftId(state),
  goal: PlaybookSelectors.currentGoal(state),
  goalRouting: PlaybookSelectors.currentGoalRouting(state),
  goals: PlaybookSelectors.goals(state),
  isMessageTypePlaybook: PlaybookSelectors.isMessageTypePlaybook(state),
  isCaptureTypePlaybook: PlaybookSelectors.isCaptureTypePlaybook(state),
  isCtaTypePlaybook: PlaybookSelectors.isCtaTypePlaybook(state),
  isChatTypePlaybook: PlaybookSelectors.isChatTypePlaybook(state),
  isOutboundPlaybook: PlaybookSelectors.isOutboundPlaybook(state),
  isABMPlaybook: PlaybookSelectors.isABMPlaybook(state),
  hasRoutingAction: PlaybookSelectors.getHasRoutingAction(state),
  isMeetingCta: PlaybookSelectors.isMeetingCta(state),
  classificationOptions: ClassificationSelectors.classificationOptions(state),
  orgUsersById: UserSelectors.orgUsersById(state),
  userIdsForRules: RulesSelectors.userIdsForRules(state),
  calendarConnectedAgentIds: MeetingSelectors.getAgentIdsWithConnectedCalendars(state),
  reportType: PlaybookSelectors.reportType(state),
})

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators(
    {
      createClassifications: ClassificationActions.createClassifications,
      setGoalClassification: PlaybookActions.setGoalClassification,
      setCurrentGoalId: PlaybookActions.setCurrentGoalId,
      setMessageActionBody: PlaybookActions.setGoalMessage,
      showGoalActionModal: PlaybookActions.showGoalActionModal,
      showGoalRoutingModal: PlaybookActions.showGoalRoutingModal,
      selectGoalAction: PlaybookActions.selectGoalAction,
      selectGoalRoutingOptionUI: PlaybookActions.selectGoalRoutingOptionUI,
      removeGoalAction: PlaybookActions.removeGoalAction,
      deleteGoal: PlaybookActions.deleteGoal,
    },
    dispatch
  ),
})

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  branch(
    props => !props.goal || !props.classificationOptions.length || !props.orgUsersById.size,
    renderNothing
  ),
  withProps(props => ({
    copy: playbooksCopy[props.reportType].goals,
    classificationDetails: props.classificationOptions.find(
      classification => classification.value === props.goal.classification.get('classificationId')
    ),
    isGlobalRouting: props.goalRouting.get('type') === RoutingType.GLOBAL,
    isSingleRouting: props.goalRouting.get('type') === RoutingType.SINGLE,
    isGroupRouting: props.goalRouting.get('type') === RoutingType.GROUP,
    isNoRouting: props.goalRouting.get('type') === RoutingType.NONE,
    isMessage: props.goal && !!props.goal.get('message'),
    isCQL: props.goal && !!props.goal.get('markCQL'),
    cqlScore: props.goal && +_.get(props.goal.toJS(), 'markCQL.score', 0),
    isScheduling: props.goal && !!props.goal.get('schedule'),
    isStatusChange: props.goal && !!props.goal.get('statusChange'),
    isLastGoal: props.goals && props.goals.size == 1,
    singleRoutingTargetUser:
      props.goalRouting.get('type') === RoutingType.SINGLE &&
      props.orgUsersById.find(user => user.id === props.goalRouting.get('userId')),
    usersForGlobalRules:
      props.userIdsForRules &&
      props.userIdsForRules.map(ruleUserId => props.orgUsersById.find(user => user.id === ruleUserId)),
    groupRoutingTargetUsers: (props.goalRouting.get('userIds') || List()).map(userId =>
      props.orgUsersById.find(user => user.id === userId)
    ),
  })),
  lifecycle({
    componentWillMount() {
      const { actions, goalRouting } = this.props
      actions.selectGoalRoutingOptionUI(goalRouting || PlaybookModels.RoutingOption())
    },
  }),
  withHandlers({
    singleRoutingInvalidUsers: props => () => {
      if (!props.isScheduling || !props.goal || !props.orgUsersById) return []
      const userId = props.goal.routing.get('userId')
      if (!props.calendarConnectedAgentIds.includes(userId)) {
        return [props.orgUsersById.get(userId)]
      }
      return []
    },
    globalRoutingInvalidUsers: props => () => {
      if (!props.isScheduling) return []
      const userIds = props.userIdsForRules ? props.userIdsForRules : []
      return userIds
        .filter(id => !props.calendarConnectedAgentIds.includes(id))
        .map(id => props.orgUsersById.get(id))
        .filter(Boolean)
    },
    onGoalDeleteClick: props => () => {
      const { actions: { deleteGoal }, goals, goal: { id }, playbookId } = props
      const redirectToGoalId = goals.reduce((redirectId, goal) => {
        return !redirectId && goal.id !== id ? goal.id : redirectId
      }, false)
      deleteGoal({ goalId: id, playbookId })
      props.router.replace(`/playbooks/${playbookId}/edit/goal/${redirectToGoalId}`)
    },
    onEditRoutingClick: props => () => {
      const { actions: { showGoalRoutingModal }, goal: { id }} = props
      showGoalRoutingModal(id)
    },
    onEditActionClick: props => actionProperty => {
      const { actions: { showGoalActionModal, selectGoalAction }, goal } = props
      selectGoalAction({ [actionProperty]: goal.get(actionProperty) })
      showGoalActionModal(goal.id)
    },
    onDeleteActionClick: props => actionProperty => {
      const { actions: { removeGoalAction }, goal: { id }} = props
      removeGoalAction({ goalId: id, goalActionProperty: actionProperty })
    },
    onAddActionClick: props => () => {
      const { actions: { showGoalActionModal }, goal: { id }} = props
      showGoalActionModal(id)
    },
    onClassificationChange: props => event => {
      if (!event || !event.value || event.value.includes(' ')) return false
      props.actions.setGoalClassification({
        goalId: props.goal.id,
        classificationId: event.value,
      })
    },
    onNewClassificationClick: props => ({ label }) => {
      props.actions.createClassifications([{ name: label }])
    },
    onMessageActionBodyChange: props => body => {
      props.actions.setMessageActionBody({ goalId: props.goal.id, body })
    },
  })
)(Goal)



// WEBPACK FOOTER //
// ./src/scripts/components/playbook/components/Goal/index.js